import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DriverPage from "./DriverPage";
import { Helmet } from "react-helmet";
import { fetchAllTrips } from "../../Services/ApiCallsServices/DriverDashboardApi";
import { useAuth } from "../../context/AuthContext";
import { useDispatch } from 'react-redux';
import { setTripReference } from '../../Reducers/tripSlice';
import { FiMapPin, FiArrowRightCircle, FiTruck } from 'react-icons/fi';
import { MdOutlinePendingActions } from 'react-icons/md';
import { FaRegClock } from 'react-icons/fa6';

const RideSummary = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalRides, setTotalRides] = useState(0);
    const [trips, setTrips] = useState([]);
    const [error, setError] = useState(null);
    const { auth } = useAuth();

    const email = auth.user.email ?? "";

    useEffect(() => {
        if (!email) {
            setError("failed to retrieve user email");
            return;
        }

        (async () => {
            try {
                const data = await fetchAllTrips();
                if (Array.isArray(data.trips)) {
                    setTrips(data.trips);

                    const completed = data.trips.filter(t => t.status === 'Completed');
                    const sum = completed.reduce((acc, t) => acc + (t.amount || 0), 0);
                    setTotalAmount(sum);
                    setTotalRides(completed.length);
                } else {
                    console.error("No trips found in response");
                    setTrips([]);
                }
            } catch (err) {
                console.error("Error fetching trips:", err);
                setError(err.message || "Error fetching trips");
                setTrips([]);
            }
        })();
    }, [email]);

    const handleOngoingTripClick = reference => {
        dispatch(setTripReference(reference));
        navigate('/trip-progress');
    };

    const previousTrips = useMemo(
        () => trips.filter(t => ['Completed','Cancelled'].includes(t.status)),
        [trips]
    );
    const ongoingTrips = useMemo(
        () => trips.filter(t =>
            ['Accepted','Driving to Pickup','Driving to Destination'].includes(t.status)
        ),
        [trips]
    );
    const upcomingTrips = useMemo(
        () => trips.filter(t => ['Pending','Not Started'].includes(t.status)),
        [trips]
    );

    return (
        <DriverPage>
            <Helmet><title>Driver Dashboard | Storage Sprint</title></Helmet>

            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-md">
                    <p>Error: {error}</p>
                </div>
            )}

            <div className="min-h-screen bg-gray-100 px-4 py-6">
                {/* Earnings Summary */}
                <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-medium text-yellow-600 mb-2">Today’s Earnings</h2>
                    <h1 className="text-4xl font-extrabold text-gray-800 tracking-tight">
                        R{isNaN(Number(totalAmount)) ? '0.00' : Number(totalAmount).toFixed(2)}
                    </h1>
                </div>

                {/* Ongoing Trips */}
                <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-yellow-600 flex items-center gap-2">
                            <FiTruck /> Ongoing Trips
                        </h3>
                        <button
                            className="px-4 py-1.5 bg-yellow-100 text-yellow-700 font-medium rounded-md cursor-not-allowed"
                            disabled
                        >
                            Navigation
                        </button>
                    </div>

                    {ongoingTrips.length > 0 ? (
                        <div className="space-y-4">
                            {ongoingTrips.map((trip, idx) => (
                                <div
                                    key={idx}
                                    className="p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 cursor-pointer transition"
                                    onClick={() => handleOngoingTripClick(trip.reference)}
                                >
                                    <div className="flex justify-between">
                                        <p className="font-semibold text-gray-800">{trip.customerName}</p>
                                        <span className="text-gray-600 text-sm">{trip.status}</span>
                                    </div>
                                    <div className="text-gray-600 mt-2 space-y-1 text-sm">
                                        <p><FiMapPin className="inline mr-2" />From: {trip.pickupLocation}</p>
                                        <p><FiMapPin className="inline mr-2" />To: {trip.dropoffLocation}</p>
                                    </div>
                                    <p className="text-right text-lg font-bold text-gray-700 mt-2">
                                        R{trip.amount != null ? Number(trip.amount).toFixed(2) : '0.00'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4 italic">No ongoing trips</p>
                    )}
                </div>

                {/* Upcoming Trips */}
                {upcomingTrips.length > 0 && (
                    <div
                        className="bg-yellow-50 border border-yellow-100 rounded-xl shadow-sm p-4 mb-6 flex justify-between items-center hover:bg-yellow-100 cursor-pointer transition"
                        onClick={() => navigate('/available-trips')}
                    >
                        <p className="flex items-center font-medium text-yellow-700">
                            <MdOutlinePendingActions className="mr-2 text-xl" />
                            Upcoming Trips ({upcomingTrips.length})
                        </p>
                        <FiArrowRightCircle className="text-yellow-600 text-xl" />
                    </div>
                )}

                {/* Previous Trips */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-yellow-600 mb-4 flex items-center gap-2">
                        <FaRegClock /> Previous Trips
                    </h3>

                    {previousTrips.length > 0 ? (
                        <div className="space-y-4">
                            {previousTrips.map((trip, idx) => (
                                <div
                                    key={idx}
                                    className="p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition"
                                >
                                    <div className="flex justify-between">
                                        <p className="font-semibold text-gray-800">{trip.customerName}</p>
                                        <span className="text-gray-600 text-sm">{trip.status}</span>
                                    </div>
                                    <div className="text-gray-600 mt-2 space-y-1 text-sm">
                                        <p><FiMapPin className="inline mr-2" />From: {trip.pickupLocation}</p>
                                        <p><FiMapPin className="inline mr-2" />To: {trip.dropoffLocation}</p>
                                    </div>
                                    <p className="text-right text-lg font-bold text-gray-700 mt-2">
                                        R{trip.amount != null ? Number(trip.amount).toFixed(2) : '0.00'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4 italic">No previous trips</p>
                    )}
                </div>

            </div>
        </DriverPage>
    );
};

export default RideSummary;
